import type { DataIsland } from "./types";

/**
 * Find connected components of `true` cells in a 2D boolean grid using
 * 4-connectivity (up/down/left/right). Returns one bounding box per
 * component, sorted by (minRow, minCol). Indices are relative to the grid
 * (0-based, top-left = 0,0); callers translate to sheet-absolute by adding
 * the used-range origin.
 */
export function findIslands(occupied: boolean[][]): Array<Omit<DataIsland, "address">> {
  const rows = occupied.length;
  const cols = rows > 0 ? occupied[0].length : 0;
  if (rows === 0 || cols === 0) return [];

  // Flat 1D visited array — faster than nested arrays for big sheets.
  const visited = new Uint8Array(rows * cols);
  const idx = (r: number, c: number): number => r * cols + c;

  const islands: Array<Omit<DataIsland, "address">> = [];
  // Reusable queue stored as a Int32Array-style flat array of pairs
  const queue: number[] = [];

  for (let r0 = 0; r0 < rows; r0++) {
    for (let c0 = 0; c0 < cols; c0++) {
      if (!occupied[r0]?.[c0] || visited[idx(r0, c0)]) continue;

      visited[idx(r0, c0)] = 1;
      queue.length = 0;
      queue.push(r0, c0);
      let head = 0;

      let minRow = r0;
      let maxRow = r0;
      let minCol = c0;
      let maxCol = c0;
      let cellCount = 0;

      while (head < queue.length) {
        const r = queue[head++];
        const c = queue[head++];
        cellCount++;
        if (r < minRow) minRow = r;
        if (r > maxRow) maxRow = r;
        if (c < minCol) minCol = c;
        if (c > maxCol) maxCol = c;

        // 4-neighbours
        if (r > 0 && occupied[r - 1][c] && !visited[idx(r - 1, c)]) {
          visited[idx(r - 1, c)] = 1;
          queue.push(r - 1, c);
        }
        if (r + 1 < rows && occupied[r + 1][c] && !visited[idx(r + 1, c)]) {
          visited[idx(r + 1, c)] = 1;
          queue.push(r + 1, c);
        }
        if (c > 0 && occupied[r][c - 1] && !visited[idx(r, c - 1)]) {
          visited[idx(r, c - 1)] = 1;
          queue.push(r, c - 1);
        }
        if (c + 1 < cols && occupied[r][c + 1] && !visited[idx(r, c + 1)]) {
          visited[idx(r, c + 1)] = 1;
          queue.push(r, c + 1);
        }
      }

      islands.push({ minRow, minCol, maxRow, maxCol, cellCount });
    }
  }

  islands.sort((a, b) => a.minRow - b.minRow || a.minCol - b.minCol);
  return islands;
}
